import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Package } from 'lucide-react';
import { getImageUrl } from '../utils/api';

interface ProductImageProps {
  src: string;
  alt: string;
  className?: string;
  fallbackClassName?: string;
  onError?: () => void;
  cacheBust?: boolean;
}

const ProductImage: React.FC<ProductImageProps> = ({
  src,
  alt,
  className = '',
  fallbackClassName = '',
  onError,
  cacheBust = false
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Memoize the final src to prevent unnecessary re-renders
  const finalSrc = useMemo(() => {
    if (!src) return '';
    
    // Validate and clean the source URL
    let cleanSrc = src.trim();
    
    // Check for malformed URLs or suspicious content
    if (cleanSrc.includes('<') || cleanSrc.includes('>') || cleanSrc.includes('class=') || cleanSrc.includes('hutton')) {
      console.error('Malformed image src detected:', cleanSrc);
      return ''; // Return empty to trigger fallback
    }
    
    // Get the full image URL (handles both Cloudinary and local images)
    const fullImageUrl = getImageUrl(cleanSrc);
    
    // Only add cache-busting if explicitly requested
    if (cacheBust && !fullImageUrl.startsWith('http')) {
      return `${fullImageUrl}?t=${Date.now()}`;
    }
    
    return fullImageUrl;
  }, [src, cacheBust]);

  // Reset states when src changes and preload
  useEffect(() => {
    setHasError(false);
    setIsLoading(true);
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    if (!finalSrc) {
      // No src means no image, hide loading immediately
      setIsLoading(false);
      return;
    }
    
    // Preload image to check if it's already cached
    const img = new Image();
    
    img.onload = () => {
      console.log('Image preloaded/cached:', finalSrc);
      setIsLoading(false);
      setHasError(false);
    };
    
    img.onerror = () => {
      console.log('Image preload failed:', finalSrc);
      setHasError(true);
      setIsLoading(false);
    };
    
    // Start preloading
    img.src = finalSrc;
    
    // Set fallback timeout for edge cases
    timeoutRef.current = setTimeout(() => {
      console.log('Image loading timeout reached for:', finalSrc);
      setIsLoading(false);
    }, 5000);
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [finalSrc]);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error('ProductImage failed to load:', {
      originalSrc: src,
      finalSrc: finalSrc,
      alt: alt,
      error: e.type,
      timestamp: new Date().toISOString()
    });
    
    // Clear timeout since we're handling the error
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Try to determine the error cause
    if (finalSrc.includes('onrender.com') || (!finalSrc.includes('cloudinary.com') && finalSrc.includes('http'))) {
      console.warn('Server deployment may have reset uploaded files. Using cloud storage for reliability.');
    }
    
    setHasError(true);
    setIsLoading(false);
    if (onError) {
      onError();
    }
  };

  const handleLoad = () => {
    console.log('Image loaded successfully:', finalSrc);
    
    // Clear timeout since image loaded successfully
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    setIsLoading(false);
    setHasError(false);
  };

  // Calculate responsive icon size based on container
  const getIconSize = () => {
    // Check for large containers (product detail main image, full height containers)
    if (className.includes('h-full') || className.includes('w-full h-full')) {
      return 'h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24'; // Large containers (product detail)
    }
    // Medium containers (product listing, medium height containers)
    if (className.includes('h-40') || className.includes('h-48')) {
      return 'h-12 w-12'; // Medium containers
    }
    // Small containers (thumbnails)
    if (className.includes('h-16') || className.includes('h-20')) {
      return 'h-8 w-8'; // Small containers (thumbnails)
    }
    // Default medium size for unknown containers
    return 'h-10 w-10'; // Default size
  };

  const getTextSize = () => {
    // Larger text for large containers
    if (className.includes('h-full') || className.includes('w-full h-full')) {
      return 'text-sm sm:text-base md:text-lg font-medium'; // Larger text for large containers
    }
    // Medium text for medium containers
    if (className.includes('h-40') || className.includes('h-48')) {
      return 'text-sm'; // Medium text
    }
    return 'text-xs'; // Default small text
  };

  // Don't render anything if no src provided or malformed
  if (!src || !finalSrc) {
    return (
      <div className={`flex items-center justify-center bg-gray-200 dark:bg-gray-600 ${fallbackClassName || className}`}>
        <div className="text-center">
          <Package className={`${getIconSize()} text-gray-400 dark:text-gray-500 mx-auto mb-2`} />
          <p className={`${getTextSize()} text-gray-500 dark:text-gray-400`}>No image</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className={`flex items-center justify-center bg-gray-200 dark:bg-gray-600 ${fallbackClassName || className}`}>
        <div className="text-center">
          <Package className={`${getIconSize()} text-gray-400 dark:text-gray-500 mx-auto mb-2`} />
          <p className={`${getTextSize()} text-gray-500 dark:text-gray-400`}>Image unavailable</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 dark:bg-gray-600">
          <div className="animate-pulse">
            <Package className={`${getIconSize()} text-gray-400 dark:text-gray-500`} />
          </div>
        </div>
      )}
      <img
        src={finalSrc}
        alt={alt || 'Product image'}
        className={`w-full h-full object-contain ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}
        onError={handleError}
        onLoad={handleLoad}
        loading="lazy"
      />
    </div>
  );
};

export default ProductImage;